import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import AnimatedModal from "components/layout/motion/AnimatedModal";
import FunctionalButton from "components/layout/FunctionalButton";
import { ReactComponent as CloseIcon } from "assets/svgs/Close.svg";
import { getStcmMapListBaseUrl, getStorageMapPreviewBmpUrl } from "lib/consts/stcmMapList";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";
import useFleetArcId from "lib/utils/useFleetArcId";
import {
  fetchMissionStorageMapPickerGroups,
  type MissionStorageLoadMapPickerRow,
  type MissionStorageMapPickerGroup,
} from "lib/mission/missionStorageMaps";

interface LoadMapDialogProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: (selection: MissionStorageLoadMapPickerRow) => void;
  /** When set, STCM map list requests use this host (fleet / single-ARC). */
  arcHost?: string | null;
}

const storageRowClass = (active: boolean) =>
  `w-full rounded-r5 border px-3 py-2 text-left text-h5 transition-colors ${
    active
      ? "border-primary2 bg-black80 text-white"
      : "border-black80 bg-black70 text-black40 hover:border-black60 hover:bg-black80"
  }`;

/** Horizontal variant chips — matches Map Manager (MapView). */
const variantChipRowClass = (active: boolean) =>
  `shrink-0 min-w-[8.5rem] max-w-[11rem] rounded-r5 border px-3 py-2 text-left text-h5 transition-colors ${
    active
      ? "border-primary2 bg-black80 text-white"
      : "border-black80 bg-black70 text-black40 hover:border-black60 hover:bg-black80"
  }`;

function findGroupForRowId(
  groups: MissionStorageMapPickerGroup[],
  rowId: string
): MissionStorageMapPickerGroup | null {
  for (const g of groups) {
    if (g.base.rowId === rowId) return g;
    if (g.variants.some((v) => v.rowId === rowId)) return g;
  }
  return null;
}

function findRowById(
  groups: MissionStorageMapPickerGroup[],
  rowId: string
): MissionStorageLoadMapPickerRow | null {
  for (const g of groups) {
    if (g.base.rowId === rowId) return g.base;
    const variant = g.variants.find((v) => v.rowId === rowId);
    if (variant) return variant;
  }
  return null;
}

const LoadMapDialog: React.FC<LoadMapDialogProps> = ({ visible, onClose, onConfirm, arcHost }) => {
  const { t } = useTranslation(["missions", "common"]);
  const isSecure = useIsHttpSecure();
  const arcId = useFleetArcId();
  const [groups, setGroups] = useState<MissionStorageMapPickerGroup[]>([]);
  const [focusedMapId, setFocusedMapId] = useState<string | null>(null);
  const [selectedRowId, setSelectedRowId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);

  const focusedGroup = useMemo(
    () => groups.find((g) => g.mapId === focusedMapId) ?? null,
    [groups, focusedMapId]
  );

  useEffect(() => {
    if (!visible) return;

    let cancelled = false;
    const fetchMaps = async () => {
      setLoading(true);
      setError(null);
      try {
        const fetched = await fetchMissionStorageMapPickerGroups(arcHost, isSecure, arcId);
        if (cancelled) return;
        setGroups(fetched);
        const first = fetched[0];
        setFocusedMapId((prev) => {
          if (prev && fetched.some((g) => g.mapId === prev)) return prev;
          return first?.mapId ?? null;
        });
        setSelectedRowId((prev) => {
          if (prev && findRowById(fetched, prev)) return prev;
          return first?.base.rowId || "";
        });
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : t("dialogs.mapsLoadFailed"));
        setGroups([]);
        setFocusedMapId(null);
        setSelectedRowId("");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void fetchMaps();
    return () => {
      cancelled = true;
    };
  }, [visible, arcHost, isSecure, arcId]);

  useEffect(() => {
    setPreviewFailed(false);
  }, [selectedRowId]);

  const selectedRow = findRowById(groups, selectedRowId);
  const stcmBaseUrl = getStcmMapListBaseUrl(arcHost, isSecure, arcId);
  const previewUrl =
    stcmBaseUrl && selectedRow
      ? getStorageMapPreviewBmpUrl(stcmBaseUrl, selectedRow.previewRelPath)
      : null;

  const handleBaseSelect = (group: MissionStorageMapPickerGroup) => {
    setFocusedMapId(group.mapId);
    setSelectedRowId(group.base.rowId);
  };

  return (
    <AnimatedModal
      visible={visible}
      onClose={onClose}
      className="w-full max-w-4xl rounded-r10 border border-black80 bg-black90 p-4"
      zIndexClassName="z-[210]"
    >
      <div className="relative">
        <FunctionalButton
          icon={<CloseIcon />}
          className="absolute right-3 top-3"
          onClick={onClose}
        />
        <h3 className="text-h4 mb-3">{t("dialogs.loadMapTitle")}</h3>
        <p className="text-h5 mb-3 text-black40">{t("dialogs.loadMapDescription")}</p>

        <div className="mb-4 grid grid-cols-2 gap-4">
          <div className="flex max-h-[22rem] flex-col rounded-r5 border border-black80 bg-black70/30 p-2">
            <p className="text-h5 mb-2 shrink-0 font-semibold text-black40">
              {t("dialogs.storageHeading")}
            </p>
            <ul className="min-h-0 flex-1 space-y-1 overflow-y-auto overscroll-contain pr-1">
              {loading ? (
                <li className="text-h5 py-2 text-black60">{t("dialogs.loadingMaps")}</li>
              ) : error ? (
                <li className="text-h5 py-2 text-black40">
                  {t("dialogs.loadListFailed", { error })}
                </li>
              ) : groups.length === 0 ? (
                <li className="text-h5 py-2 text-black60">{t("dialogs.noMapsFound")}</li>
              ) : (
                groups.map((group) => (
                  <li key={group.mapId}>
                    <button
                      type="button"
                      className={storageRowClass(focusedMapId === group.mapId)}
                      onClick={() => handleBaseSelect(group)}
                    >
                      <span className="block font-semibold">
                        {group.base.label || t("dialogs.untitledMap")}
                      </span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>

          <div className="flex max-h-[22rem] min-h-[18rem] flex-col rounded-r5 border border-black80 bg-black70/30 p-2">
            <p className="text-h5 mb-2 shrink-0 font-semibold text-black40">
              {t("dialogs.preview")}
            </p>
            {!selectedRow ? (
              <div className="flex min-h-[10rem] flex-1 items-center justify-center rounded-r5 border border-dashed border-black80 bg-black70/50">
                <p className="text-h5 text-black60">{t("dialogs.selectToPreview")}</p>
              </div>
            ) : !previewUrl ? (
              <div className="flex min-h-[10rem] flex-1 items-center justify-center rounded-r5 border border-dashed border-black80 bg-black70/50">
                <p className="text-h5 text-center text-black60">
                  {t("dialogs.previewUnavailable")}
                  <br />
                  {t("dialogs.previewApiNotConfigured")}
                </p>
              </div>
            ) : previewFailed ? (
              <div className="flex min-h-[10rem] flex-1 items-center justify-center rounded-r5 border border-dashed border-black80 bg-black70/50">
                <p className="text-h5 text-black60">{t("dialogs.noPreviewImage")}</p>
              </div>
            ) : (
              <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-r5 border border-black80 bg-black70 p-2">
                <img
                  key={selectedRow.rowId}
                  src={previewUrl}
                  alt={t("dialogs.previewAlt", { label: selectedRow.label })}
                  className="max-h-full w-full max-w-full object-contain"
                  onError={() => setPreviewFailed(true)}
                />
              </div>
            )}

            {focusedGroup ? (
              <div className="mt-2 flex shrink-0 flex-col border-t border-black80 pt-2">
                <p className="text-h5 mb-1.5 shrink-0 font-semibold text-black40">
                  {t("dialogs.variantsHeading")}
                </p>
                {focusedGroup.variants.length > 0 ? (
                  <ul className="flex min-h-[5rem] flex-nowrap items-center gap-2 overflow-x-auto overflow-y-hidden overscroll-contain pb-1 pr-1">
                    {focusedGroup.variants.map((variant) => (
                      <li key={variant.rowId} className="shrink-0">
                        <button
                          type="button"
                          className={variantChipRowClass(selectedRowId === variant.rowId)}
                          onClick={() => setSelectedRowId(variant.rowId)}
                        >
                          <span className="flex flex-col items-stretch gap-0.5 text-left">
                            <span className="line-clamp-2 break-words">
                              {variant.label || t("dialogs.editedVariant")}
                            </span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-h5 py-1 text-black60">{t("dialogs.noVariants")}</p>
                )}
              </div>
            ) : null}
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-black80 pt-3">
          <FunctionalButton
            className="rounded-r5 border border-black80 bg-black70 px-4 py-2 hover:bg-black80"
            title={t("common:cancel")}
            onClick={onClose}
          />
          <FunctionalButton
            className="hover:bg-primary1 rounded-r5 bg-primary2 px-4 py-2"
            title={t("actions.addLoadMap")}
            disabled={!selectedRow}
            onClick={() => {
              if (!selectedRow) return;
              onConfirm(selectedRow);
            }}
          />
        </div>
      </div>
    </AnimatedModal>
  );
};

export default LoadMapDialog;
